import { useTranslation } from 'next-i18next';
import { useCallback, useRef, useState } from 'react';
import { useDeviceType } from '@hotelplan/libs.context.device-type';
import { useObjectId } from '@hotelplan/libs.context.object-id';
import { useToggleState } from '@hotelplan/libs.hooks-react';
import { mapBedbankToCode } from '@hotelplan/libs.mappers';
import { useSearchState } from '@hotelplan/libs.search-state';
import {
  trackORLeCommerce,
  ECommerceEventType,
  TECommerceEventData,
} from '@hotelplan/libs.track.orl';
import { trackCheckOffer } from '@hotelplan/libs.tracking';
import {
  SingleOrlItem,
  DeviceType,
  OrlCheckoutStatus,
  OrlFlightAlternative,
  TravelType,
} from '@hotelplan/platform-graphql-api';
import { ISrlState } from 'components/domain/srl/SRL.types';
import { useOrlCheckoutLazyQuery } from 'graphql/orl/ORLCheckout.generated';
import { mapSingleOrlItemToTrackableOfferMetaData } from './Orl.mappers';
import { ICheckoutOffer } from './Orl.types';
import useMetaSearchParam from './useMetaSearchParam';

function calculatePriceDifference(
  price: number,
  deviationPrice: number
): string {
  const priceDifference = ((deviationPrice - price) / price) * 100;
  return `${Math.round(priceDifference)}%`;
}

export type ORLCheckoutState = ReturnType<typeof useORLCheckout>;

export default function useORLCheckout(productName?: string) {
  const [, { language }] = useTranslation();
  const { id: objectId } = useObjectId();
  const { mobile } = useDeviceType();
  const metaSearch = useMetaSearchParam();

  const now = Date.now();

  const [areCheckoutResultsShown, showCheckoutResults, closeCheckoutResults] =
    useToggleState(false);
  const [isNoResultsModalShown, openNoResultsModal, closeNoResultsModal] =
    useToggleState(false);

  const [waitingScreenImg, setWaitingScreenImg] = useState('');
  const [isForceClosed, forceClose, resetForceClosed] = useToggleState(false);

  const { state: searchState } = useSearchState<ISrlState>();
  const { searchControl } = searchState || {};

  const trackOfferData = useRef<TECommerceEventData>(null);
  const offerPrice = useRef<number>();
  const bedbankRef = useRef<string>();

  const [orlCheckoutLazyQuery, { data, loading: checkoutLoading }] =
    useOrlCheckoutLazyQuery({
      ssr: false,
      fetchPolicy: 'network-only',
      onCompleted: res => {
        if (isForceClosed) return;

        const isSucceeded = [
          OrlCheckoutStatus.CheckoutSuccess,
          OrlCheckoutStatus.CheckoutOfferChanged,
        ].includes(res.orl.checkout.checkoutStatus);

        const isChangedOffer = Boolean(res.orl.checkout.changedOffer);
        const changedPrice = +res.orl.checkout.changedOffer?.totalPrice.amount;
        const bedbank = mapBedbankToCode(bedbankRef?.current);

        const isAddBedbank =
          ([
            OrlCheckoutStatus.CheckoutUnavailable,
            OrlCheckoutStatus.CheckoutStatusNotSet,
          ].includes(res.orl.checkout.checkoutStatus) ||
            isChangedOffer) &&
          bedbank;

        trackCheckOffer({
          changes: {
            flight: res.orl.checkout.flightChanged,
            hotel: !isSucceeded,
            price: res.orl.checkout.priceChanged,
          },
          success: isSucceeded,
          performance: { time: Date.now() - now },
          horst: {
            price: offerPrice.current || '',
            ...(isChangedOffer
              ? {
                  deviationPrice: changedPrice || '',
                  priceDifference: calculatePriceDifference(
                    offerPrice.current,
                    changedPrice
                  ),
                }
              : {}),
            ...(isAddBedbank
              ? {
                  bedbank,
                }
              : {}),
          },
          product: isChangedOffer
            ? mapSingleOrlItemToTrackableOfferMetaData(
                res.orl.checkout.changedOffer,
                objectId
              )
            : undefined,
          ...(!isChangedOffer
            ? {
                productId: objectId,
              }
            : {}),
          type:
            searchControl?.travelType === TravelType.Package
              ? 'package'
              : 'hotel',
        });
        if (trackOfferData?.current !== null) {
          trackORLeCommerce(trackOfferData.current);
          trackOfferData.current = null;
        }

        resetForceClosed();
        switch (res.orl.checkout.checkoutStatus) {
          case OrlCheckoutStatus.CheckoutOfferChanged:
            showCheckoutResults();
            break;
          case OrlCheckoutStatus.CheckoutSuccess: {
            if (res.orl.checkout.link) {
              window.location.href = res.orl.checkout.link.url;
            }
            break;
          }
          case OrlCheckoutStatus.CheckoutUnavailable:
          case OrlCheckoutStatus.CheckoutStatusNotSet:
            openNoResultsModal();
            break;

          default:
            break;
        }
      },
    });

  const onCheckout = useCallback(
    (offer: ICheckoutOffer, id: string, isTrackECommerce?: boolean) => {
      return (alternativeFlight?: OrlFlightAlternative) => {
        offerPrice.current =
          +alternativeFlight?.totalPrice.amount || +offer.totalPrice.amount;
        bedbankRef.current = offer.provider;
        resetForceClosed();
        if (isTrackECommerce) {
          trackOfferData.current = {
            offer: offer as SingleOrlItem,
            id,
            language,
            eventType: ECommerceEventType.ADD_TO_CARD,
            productName: productName || '',
          };
        }
        orlCheckoutLazyQuery({
          variables: {
            backUrl: window.location.href,
            deviceType: mobile ? DeviceType.Mobile : DeviceType.Desktop,
            id,
            offerId: alternativeFlight?.offerId || offer.id,
            bookingId: offer.originalBookingId,
            metaSearch,
          },
        });
        setWaitingScreenImg(
          alternativeFlight
            ? alternativeFlight.waitingScreenImage
            : offer.waitingScreenImage || ''
        );
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return {
    onCheckout,
    isNoResultsModalShown,
    closeCheckoutResults,
    areCheckoutResultsShown,
    closeNoResultsModal,
    data,
    waitingScreenImg,
    isForceClosed,
    forceClose,
    checkoutLoading,
  };
}
